/** The bottom panel: the cave's settings as a form, and the live validation messages under it. */

import { BLACK, BLUE, COLOUR_NAMES, DARK_GREY, GREY, WHITE, YELLOW } from '../../render/palette';
import { COLOUR_SLOTS, NUMBER_FIELDS } from '../model/fields';
import type { FieldId } from '../model/fields';
import { MAX_FILL_ITEMS } from '../model/doc';
import type { TextField } from '../model/text';
import type { KitApi } from './api';
import type { FormItem } from './formItems';
import { FORM_ITEMS, formIndexOf } from './formItems';
import { drawIssues } from './issues';
import { FORM, FORM_COLS, FORM_ROWS, INK } from './layout';
import { drawCellAt } from './look';
import { arrow, fit, panel, stepper } from './widgets';

function item(id: string): FormItem {
  return FORM_ITEMS[formIndexOf(id)] ?? (FORM_ITEMS[0] as FormItem);
}

function focused(k: KitApi, id: string): boolean {
  return k.focus === 'form' && FORM_ITEMS[k.formIndex]?.id === id;
}

/** Point the keyboard focus at an item the mouse used, so the keys carry on from there. */
function touch(k: KitApi, id: string): void {
  if (k.focus === 'form') k.formIndex = formIndexOf(id);
}

function textBox(k: KitApi, field: TextField, x: number, y: number, w: number): void {
  const { ui } = k;
  const s = ui.screen;
  const value = k.st.doc[field];
  const editing = k.editing === field;
  const hot = ui.hot(field);
  s.text(field === 'name' ? 'NAME' : 'HINT', x, y, focused(k, field) || editing ? YELLOW : INK.text);
  const bx = x + 40;
  s.fill(bx, y - 2, w, 11, editing ? BLUE : DARK_GREY);
  s.box(bx, y - 2, w, 11, editing ? YELLOW : hot || focused(k, field) ? GREY : DARK_GREY);
  const room = Math.floor((w - 6) / 8);
  const shown = editing ? value.slice(Math.max(0, value.length - room + 1)) : fit(value, room);
  s.text(shown, bx + 3, y, editing ? WHITE : INK.text);
  if (editing && (ui.anim >> 3) % 2 === 0) s.fill(bx + 3 + shown.length * 8, y, 7, 7, YELLOW);
  if (!editing && value === '' && field === 'hint') s.text('(NONE)', bx + 3, y, GREY);
  const it = item(field);
  ui.add({ x: bx, y: y - 2, w, h: 11, id: field, tip: it.tip(k), click: () => {
    touch(k, field);
    it.activate?.(k);
  } });
}

function numberField(k: KitApi, id: FieldId, x: number, y: number): void {
  const f = NUMBER_FIELDS[id];
  const it = item(id);
  stepper(k.ui, x, y, f.label, String(f.get(k.st.doc)), {
    id, tip: it.tip(k), focused: focused(k, id),
    change: (d) => {
      touch(k, id);
      it.step(k, d);
    },
    ...(it.activate ? { press: () => it.activate?.(k) } : {}),
  });
}

function colourField(k: KitApi, slot: 0 | 1 | 2, x: number, y: number): void {
  const { ui } = k;
  const s = ui.screen;
  const id = `c${slot}`;
  const it = item(id);
  const c = k.st.doc.colors[slot];
  const on = focused(k, id);
  if (on) s.fill(x - 2, y - 2, 156, 12, DARK_GREY);
  s.text(COLOUR_SLOTS[slot]?.label ?? '', x, y, on ? YELLOW : INK.text);
  const step = (d: number): void => {
    touch(k, id);
    it.step(k, d);
  };
  arrow(ui, x + 51, y, -1, `${id}-`, it.tip(k), () => step(-1), (d) => step(d));
  s.fill(x + 61, y - 1, 18, 9, c);
  s.box(x + 60, y - 2, 20, 11, ui.hot(`${id}=`) ? YELLOW : GREY);
  arrow(ui, x + 83, y, 1, `${id}+`, it.tip(k), () => step(1), (d) => step(d));
  s.text(COLOUR_NAMES[c] ?? '?', x + 93, y, WHITE);
  ui.add({
    x: x + 60, y: y - 2, w: 20, h: 11, id: `${id}=`, tip: it.tip(k),
    click: (b) => step(b === 2 ? -1 : 1), wheel: (d) => step(d),
  });
}

/** A fill slot: the element's sprite in a frame; clicking sets it, right click clears it. */
function slot(k: KitApi, id: string, ch: string | null, x: number, y: number): void {
  const { ui } = k;
  const s = ui.screen;
  const it = item(id);
  const hot = ui.hot(id);
  s.fill(x, y, 16, 16, BLACK);
  if (ch !== null) drawCellAt(s, k.atlas(), ch, x, y, ui.anim);
  else s.text('+', x + 4, y + 4, hot ? YELLOW : GREY);
  s.box(x - 1, y - 1, 18, 18, hot || focused(k, id) ? YELLOW : ch === null ? DARK_GREY : GREY);
  ui.add({ x: x - 1, y: y - 1, w: 18, h: 18, id, tip: it.tip(k), click: (b) => {
    touch(k, id);
    if (b === 2) it.clear?.(k);
    else it.activate?.(k);
  }, wheel: (d) => it.step(k, -d) });
}

function fillRow(k: KitApi, y: number): void {
  const { ui } = k;
  const s = ui.screen;
  const fill = k.st.doc.fill;
  const on = FORM_ITEMS[k.formIndex]?.id.startsWith('fill') === true && k.focus === 'form';
  s.text('? FILL', FORM_COLS[0], y + 4, on ? YELLOW : INK.text);
  slot(k, 'fillBase', fill.base, 62, y);
  for (let i = 0; i < MAX_FILL_ITEMS; i++) {
    const bx = 96 + i * 96;
    const it = fill.items[i];
    if (i > fill.items.length) continue;
    s.text('+', bx - 12, y + 4, GREY);
    slot(k, `fill${i}`, it ? it[0] : null, bx, y);
    if (!it) continue;
    const fi = item(`fill${i}`);
    const step = (d: number): void => {
      touch(k, `fill${i}`);
      fi.step(k, d);
    };
    arrow(ui, bx + 21, y + 4, -1, `fill${i}-`, fi.tip(k), () => step(-1), (d) => step(-d));
    s.text(`${it[1]}%`, bx + 64, y + 4, WHITE, 'right');
    arrow(ui, bx + 66, y + 4, 1, `fill${i}+`, fi.tip(k), () => step(1), (d) => step(-d));
  }
}

export function drawForm(k: KitApi): void {
  const { ui } = k;
  const right = k.editing ? 'ENTER: DONE' : k.focus === 'form' ? 'ARROWS: CHANGE  ESC: BACK' : 'TAB: KEYBOARD';
  panel(ui, FORM, 'CAVE SETTINGS', right);
  const [r0, r1, r2, r3, r4, r5] = FORM_ROWS;
  const [c0, , c2, c3] = FORM_COLS;
  textBox(k, 'name', c0, r0, 132);
  numberField(k, 'width', c2, r0);
  numberField(k, 'height', c3, r0);
  textBox(k, 'hint', c0, r1, 434);
  const row2 = ['diamonds', 'time', 'tick', 'seed'] as const;
  const row3 = ['diamondValue', 'extraValue', 'amoebaTime', 'magicWallTime'] as const;
  row2.forEach((id, i) => numberField(k, id, FORM_COLS[i] ?? c0, r2));
  row3.forEach((id, i) => numberField(k, id, FORM_COLS[i] ?? c0, r3));
  colourField(k, 0, c0, r4);
  colourField(k, 1, c0 + 158, r4);
  colourField(k, 2, c0 + 316, r4);
  fillRow(k, r5);
  ui.screen.fill(FORM.x + 1, FORM.y + FORM.h - 38, FORM.w - 2, 1, BLUE);
  drawIssues(k, c0, FORM.y + FORM.h - 33);
}
